import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useShallow } from 'zustand/react/shallow';
// mantine
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
// TODO: replace the Sessions table and remove mantine-datatable from this page.
import { DataTable } from 'mantine-datatable';
// icons
import { ListFilter } from 'lucide-react';
// components
import { ColumnsToggle, ResponsiveButton } from '@newo/ui-kit';
import { getFiltersCount } from '@newo/toolkit/bff';
import ExportSessionsButton from '@/pages/Sessions/components/ExportSessionsButton';
import Header from '@/components/Layout/components/Header';
import Drawer from '@/components/Drawer';
import Filters from '@/pages/Sessions/components/Filters';
// utils
import { cn } from '@/utils';
import { useSessionsQuery } from '@/services/bff/bff.queries.ts';
import {
    SESSIONS_TABLE_STORE_KEY,
    PAGE_SIZES
} from '@/pages/Sessions/Sessions.utils.ts';
import { useColumns } from '@/pages/Sessions/Sessions.hooks';
import useSessionsStore from '@/pages/Sessions/Sessions.store.ts';
import { getDateRange } from '@/modules/DateTime/utils';
import { PageContainer } from '@/components/PageContainer/PageContainer.tsx';
import {
    getSessionIdColumnWidthValue,
    getResponsiveMiddleTruncationEdgeLength,
    SESSION_ID_ACCESSOR,
    SESSION_ID_COPY_BUTTON_WIDTH,
    SESSION_ID_GAP_WIDTH,
    SESSION_ID_MEASURE_SAMPLE,
    MIN_SESSION_ID_EDGE_LENGTH
} from '@/pages/Sessions/components/SessionIdCell/SessionIdCell.utils';

const START_PAGE = 1;
const DEBOUNCE_TIMEOUT = 200;
const DEFAULT_TOTAL = 0;

const Sessions = () => {
    const [isOpened, handlers] = useDisclosure();
    const sessionIdMeasureRef = useRef<HTMLSpanElement | null>(null);
    const [sessionIdEdgeLength, setSessionIdEdgeLength] = useState(
        MIN_SESSION_ID_EDGE_LENGTH
    );

    const {
        hiddenColumns,
        setHiddenColumns,
        filters,
        dateRange,
        pagination,
        setPagination
    } = useSessionsStore(
        useShallow((state) => ({
            hiddenColumns: state.hiddenColumns,
            setHiddenColumns: state.setHiddenColumns,
            filters: state.filters,
            dateRange: state.dateRange,
            pagination: state.pagination,
            setPagination: state.setPagination
        }))
    );

    const { effectiveColumns, resetColumns } = useColumns(sessionIdEdgeLength);

    const [debounced] = useDebouncedValue(filters, DEBOUNCE_TIMEOUT);

    const _dateRange = useMemo(() => {
        if (dateRange) {
            return getDateRange(dateRange);
        }
    }, [dateRange]);

    const { data, isLoading } = useSessionsQuery({
        ...debounced,
        ..._dateRange,
        ...pagination,
        is_active: false
    });
    const handlePageChange = (newPage: number) => {
        setPagination({ page: newPage });
    };

    const sessions = useMemo(() => data?.items ?? [], [data]);

    const total = data?.metadata.total ?? DEFAULT_TOTAL;
    const counter = getFiltersCount({ ...filters, dateRange }, [
        'per',
        'page',
        'is_active'
    ]);

    const handleDrawerClose = () => {
        handlers.close();
    };

    const handleFiltersToggle = () => {
        handlers.toggle();
    };

    const columns = effectiveColumns.map(({ accessor, title }) => ({
        accessor,
        title
    }));
    const sessionIdColumnWidth = useMemo(() => {
        const sessionIdColumn = effectiveColumns.find(({ accessor }) => {
            return accessor === SESSION_ID_ACCESSOR;
        });

        return getSessionIdColumnWidthValue(sessionIdColumn?.width);
    }, [effectiveColumns]);

    const handleHiddenChange = (hidden: string[]) => {
        setHiddenColumns(hidden);
    };

    const handleReset = () => {
        resetColumns();
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPagination({ per: newPageSize, page: START_PAGE });
    };

    useEffect(() => {
        const measureElement = sessionIdMeasureRef.current;

        if (!measureElement) {
            return;
        }

        const availableWidth = Math.max(
            0,
            sessionIdColumnWidth -
                SESSION_ID_COPY_BUTTON_WIDTH -
                SESSION_ID_GAP_WIDTH
        );

        const nextSessionIdEdgeLength = getResponsiveMiddleTruncationEdgeLength(
            {
                sessionId: SESSION_ID_MEASURE_SAMPLE,
                maxWidth: availableWidth,
                measureText: (value) => {
                    measureElement.textContent = value;

                    return measureElement.scrollWidth;
                }
            }
        );

        setSessionIdEdgeLength((currentValue) => {
            return currentValue === nextSessionIdEdgeLength
                ? currentValue
                : nextSessionIdEdgeLength;
        });
    }, [sessionIdColumnWidth]);

    return (
        <div className="h-full flex flex-col">
            <Helmet>
                <title>NEWO - Sessions</title>
            </Helmet>
            <Header breadcrumbs={[{ label: 'Sessions' }]}>
                <div className="flex items-center gap-4 ml-auto md:gap-4">
                    <ExportSessionsButton
                        dateRange={dateRange}
                        filters={debounced}
                        total={total}
                    />

                    <ColumnsToggle
                        columns={columns}
                        exclude={['datetime', 'actions']}
                        hidden={hiddenColumns}
                        onHiddenChange={handleHiddenChange}
                        onReset={handleReset}
                    />

                    <ResponsiveButton
                        counter={counter}
                        icon={<ListFilter />}
                        onClick={handleFiltersToggle}
                    >
                        Filters
                    </ResponsiveButton>
                </div>
            </Header>
            <PageContainer className="pt-6 flex-1 min-h-0 flex flex-col">
                <span
                    aria-hidden
                    className="pointer-events-none absolute invisible whitespace-nowrap font-mono text-xs"
                    ref={sessionIdMeasureRef}
                />
                <DataTable
                    highlightOnHover
                    pinLastColumn
                    classNames={{
                        pagination:
                            '[&_.mantine-datatable-pagination-pages]:mr-[88px]'
                    }}
                    columns={effectiveColumns}
                    fetching={isLoading}
                    idAccessor="id"
                    loaderBackgroundBlur={1}
                    loaderColor="dark"
                    page={pagination.page}
                    records={sessions}
                    recordsPerPage={pagination.per}
                    recordsPerPageOptions={PAGE_SIZES}
                    rowClassName={(session) => {
                        return cn({
                            'bg-[#ffefef] hover:bg-red-1':
                                session.result_success === false,
                            'bg-green-1 hover:bg-green-2':
                                session.result_success === true
                        });
                    }}
                    scrollAreaProps={{
                        scrollbarSize: 10,
                        type: 'always'
                    }}
                    storeColumnsKey={SESSIONS_TABLE_STORE_KEY}
                    totalRecords={total}
                    verticalAlign="top"
                    onPageChange={handlePageChange}
                    onRecordsPerPageChange={handlePageSizeChange}
                />
            </PageContainer>
            <Drawer
                name="sessions-filter"
                opened={isOpened}
                position="right"
                size="md"
                title="Filters"
                onClose={handleDrawerClose}
            >
                <Filters total={total} />
            </Drawer>
        </div>
    );
};

export default Sessions;
